/**
 * Layout utilities for workflow graphs using ELK (Eclipse Layout Kernel).
 *
 * We use elkjs' `layered` algorithm (a Sugiyama-style layered DAG layout) which
 * — unlike the previous hand-rolled recursive placer — correctly handles:
 *   - diamonds that FAN OUT then RE-CONVERGE (e.g. a decision node branching to
 *     two paths that both rejoin at `review`),
 *   - arbitrary branch counts (not just 2),
 *   - centering of each node over/under its neighbours,
 *   - orthogonal edge routing with minimal crossings.
 */

import ELK from 'elkjs/lib/elk.bundled.js';

// One reusable instance (the bundled build runs layout inline, no worker file).
const elk = new ELK();

const NODE_HEIGHT = 90; // fallback-only spacing when ELK is unavailable

// Per-node ELK footprint. Crucial for STRAIGHT horizontal edges: the left/right
// handles are pinned to each node's icon box (88px for action/decision nodes),
// while a terminal is a short pill — so ELK must know the real heights to align
// their port centers on one line. Mismatched heights (e.g. a uniform 90) put a
// pill's center above a square's center → smoothstep adds an ugly step.
function nodeSize(node) {
  if (node.type === 'terminal') return { width: 120, height: 36 };
  return { width: 96, height: 88 };
}

const ELK_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  // Left→right flow: between-layers gap is now horizontal, nodeNode is the
  // vertical gap between sibling branches. A bit more between-layers room so the
  // node labels (which sit BELOW each node) don't crowd the next column.
  'elk.layered.spacing.nodeNodeBetweenLayers': '90',
  'elk.spacing.nodeNode': '55',
  // Clearance so a routed "skip" edge's lane sits clearly off the node row.
  'elk.spacing.edgeNode': '34',
  // Tight, balanced placement that centers parents over their children.
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  // Respect the order nodes/edges were declared so branches stay stable.
  'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
  'elk.edgeRouting': 'ORTHOGONAL',
};

/**
 * Apply ELK auto-layout to React Flow elements.
 * @param {Array} nodes - React Flow nodes
 * @param {Array} edges - React Flow edges
 * @returns {Promise<{nodes, edges}>} - Layouted elements (edges unchanged)
 */
export async function getLayoutedElements(nodes, edges) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return { nodes: nodes || [], edges: edges || [] };
  }

  // Only feed ELK edges whose endpoints are real nodes in this graph (defensive
  // against stray END/START or dangling edges that would make ELK throw).
  const nodeIds = new Set(nodes.map((n) => n.id));
  const elkEdges = (edges || [])
    .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
    .map((e, i) => ({
      id: e.id || `e${i}`,
      sources: [e.source],
      targets: [e.target],
    }));

  const elkGraph = {
    id: 'root',
    layoutOptions: ELK_OPTIONS,
    children: nodes.map((n) => ({ id: n.id, ...nodeSize(n) })),
    edges: elkEdges,
  };

  try {
    const laidOut = await elk.layout(elkGraph);
    const posById = new Map(
      (laidOut.children || []).map((c) => [c.id, { x: c.x ?? 0, y: c.y ?? 0 }]),
    );
    const layoutedNodes = nodes.map((node) => ({
      ...node,
      position: posById.get(node.id) || { x: 0, y: 0 },
    }));

    // ── Straighten the backbone ───────────────────────────────────────
    // ELK balances a decision node between its children, so the main flow
    // stair-steps ~14px per branch (each forward edge then needs an ugly
    // 2-corner jog). Fix: a node that is the SOLE non-terminal in its layer
    // (ELK x-bucket) is on the backbone — align all such nodes to one center Y.
    // Terminal pills (START/END) and genuine parallel branches (2+ non-terminals
    // sharing a layer) keep ELK's vertical offset, so nothing overlaps.
    const isTerminal = (n) => n.type === 'terminal';
    const hOf = (n) => nodeSize(n).height;
    const byLayer = new Map();
    for (const n of layoutedNodes) {
      if (isTerminal(n)) continue;
      const x = Math.round(n.position.x);
      if (!byLayer.has(x)) byLayer.set(x, []);
      byLayer.get(x).push(n);
    }
    const backbone = [...byLayer.values()].filter((arr) => arr.length === 1).map((a) => a[0]);
    const backboneIds = new Set(backbone.map((n) => n.id));
    if (backbone.length >= 2) {
      const centers = backbone.map((n) => n.position.y + hOf(n) / 2).sort((a, b) => a - b);
      const targetCY = centers[Math.floor(centers.length / 2)]; // median
      for (const n of backbone) {
        n.position = { ...n.position, y: targetCY - hOf(n) / 2 };
      }
      // Also pull the entry/final terminals onto the line. START is always on
      // the spine. An END is on the spine only when (a) its source is ITSELF on
      // the backbone, and (b) that END is the source's sole successor (a natural
      // finish, e.g. generate_script → End). If the source is an OFFSET branch
      // (a parallel row that isn't the spine — e.g. `reply` sharing a layer with
      // `review`), its End must stay on that branch's row, NOT jump to center —
      // otherwise it lands on top of whatever backbone node shares the End's
      // layer (the bug: reply→End__1 overlapping the `notify_decision` Condition).
      // An early-exit End whose source also forks (a decision) likewise stays off.
      const outDeg = new Map();
      const sourceOf = new Map();
      for (const e of edges) {
        outDeg.set(e.source, (outDeg.get(e.source) || 0) + 1);
        sourceOf.set(e.target, e.source);
      }
      for (const n of layoutedNodes) {
        if (!isTerminal(n)) continue;
        const isStart = n.data?.terminal === 'start' || n.id === 'START';
        const src = sourceOf.get(n.id);
        const onSpine = isStart
          || (src && backboneIds.has(src) && outDeg.get(src) === 1);
        if (onSpine) {
          n.position = { ...n.position, y: targetCY - hOf(n) / 2 };
        }
      }
    }

    // ── Edge source handles (from FINAL positions) ────────────────────
    // Built-in smoothstep, always anchored to real handles. Pick the SOURCE
    // handle by the target's position relative to the source: target ABOVE →
    // leave from top, BELOW → bottom, same row → right. This keeps a decision's
    // forward leg straight while its other leg drops/rises cleanly. Targets are
    // entered from the left in this left→right flow (targetHandle = 'target-left').
    const cy = new Map(layoutedNodes.map((n) => [n.id, n.position.y + hOf(n) / 2]));
    const THRESH = 28;
    const layoutedEdges = edges.map((e) => {
      const sy = cy.get(e.source);
      const ty = cy.get(e.target);
      if (sy == null || ty == null) return e;
      if (ty < sy - THRESH) return { ...e, sourceHandle: 'source-top' };
      if (ty > sy + THRESH) return { ...e, sourceHandle: 'bottom' };
      return { ...e, sourceHandle: 'right' }; // same row → straight out the right
    });
    return { nodes: layoutedNodes, edges: layoutedEdges };
  } catch {
    // ELK failed (shouldn't normally) — fall back to a simple readable column so
    // the canvas is never empty / stacked at the origin.
    const layoutedNodes = nodes.map((node, i) => ({
      ...node,
      position: { x: 0, y: i * (NODE_HEIGHT + 70) },
    }));
    return { nodes: layoutedNodes, edges };
  }
}
